/** Saved delivery location from GET /api/delivery-addresses (camelCase). */
export interface DeliveryAddressDirectoryEntry {
  id: string;
  label: string;
  address: string;
  /** Well region code (e.g. SOK), must match the request well’s region. */
  region: string;
  /** <c>yard</c> | <c>wellPad</c>; default yard when missing. */
  category?: 'Yard' | 'WellPad';
}

function parseDeliveryCategory(raw: unknown): 'Yard' | 'WellPad' | undefined {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'wellpad' || s === 'well_pad' || s === 'well-pad') {
    return 'WellPad';
  }
  if (s === 'yard') {
    return 'Yard';
  }
  return undefined;
}

export function mapApiDeliveryAddress(raw: Record<string, unknown>): DeliveryAddressDirectoryEntry {
  return {
    id: String(raw.id ?? ''),
    label: String(raw.label ?? ''),
    address: String(raw.address ?? ''),
    region: String(raw.region ?? ''),
    category: parseDeliveryCategory(raw.category) ?? 'Yard'
  };
}

/** Addresses whose region matches the well’s region (case-insensitive). */
/** Label from directory, or raw id if unknown (for grids and summaries). */
export function deliveryAddressLabelForId(
  deliveryAddressId: string | undefined,
  directory: DeliveryAddressDirectoryEntry[]
): string {
  const id = deliveryAddressId?.trim();
  if (!id) {
    return '—';
  }
  const d = directory.find((x) => x.id === id);
  if (d?.label?.trim()) {
    const suffix = d.category === 'WellPad' ? ' (well pad)' : '';
    return `${d.label.trim()}${suffix}`;
  }
  return id;
}

export function deliveryAddressesForRegion(
  entries: DeliveryAddressDirectoryEntry[],
  regionCode: string | undefined
): DeliveryAddressDirectoryEntry[] {
  const r = regionCode?.trim();
  if (!r) {
    return [];
  }
  const upper = r.toUpperCase();
  return entries.filter((d) => (d.region?.trim() ?? '').toUpperCase() === upper);
}
